import { useTranslation } from 'react-i18next';
import Button from '@components/atoms/ui-kit/button.tsx';
import DisplayQrCode from '@components/profile/display-qr-code.tsx';
import { useState } from 'react';
import { Badge } from '@components/atoms/ui-kit/badge';
import { Card } from '@components/atoms/ui-kit/card';
import { useEnable2fa } from '@utils/api/users/use-enable-2fa.tsx';
import toast from 'react-hot-toast';
import Modal from '@components/atoms/modals/modal.tsx';
import { useGetMe } from '@utils/api/users/use-get-me.tsx';
import { useDisable2fa } from '@utils/api/users/use-disable-2fa.tsx';
import { useQueryClient } from 'react-query';
import { QueryKeys } from '@utils/api/query-keys.ts';
import PageHeader from '@components/layout/page-header.tsx';

const SecuritySettings = () => {
  const { data: user } = useGetMe();
  const { t } = useTranslation();
  const [show2faQrCode, setShow2faQrCode] = useState(false);
  const [token, setToken] = useState('');
  const { mutate } = useEnable2fa();
  const { mutate: disable2fa } = useDisable2fa();
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showDeactivate2fa, setShowDeactivate2fa] = useState(false);
  const queryClient = useQueryClient();

  const handleVerify = async () => {
    if (!token || token.length !== 6) {
      return;
    }

    mutate(
      { token },
      {
        onSuccess: data => {
          setToken('');
          setShow2faQrCode(false);
          setBackupCodes(data.result.backup_codes);
          setShowBackupCodes(true);
          toast.success(t('2fa-enabled'));
          queryClient.invalidateQueries(QueryKeys.user());
        },
        onError: error => {
          toast.error(
            t('2fa-error', {
              error:
                error.response?.data?.error_description || error.message || ''
            })
          );
        }
      }
    );
  };

  const handleDownloadBackupCodes = () => {
    if (!backupCodes.length) {
      return;
    }

    const blob = new Blob([backupCodes.join('\n')], {
      type: 'text/plain'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);

    toast.success(t('backup-codes-downloaded'));
    setShowBackupCodes(false);
  };

  const handleDeactivate2fa = async () => {
    if (!token || token.length === 0) {
      return;
    }

    disable2fa(
      { token, backup_code: token },
      {
        onSuccess: () => {
          setToken('');
          setShowDeactivate2fa(false);
          toast.success(t('2fa-disabled'));
          queryClient.invalidateQueries(QueryKeys.user());
        },
        onError: error => {
          toast.error(
            t('2fa-error', {
              error:
                error.response?.data?.error_description || error.message || ''
            })
          );
        }
      }
    );
  };

  return (
    <Card padding="sm" className="sm:p-6">
      <PageHeader
        title={t('security-settings')}
        caption={t('2fa-settings')}
        className="border-b-0 mb-4"
        contentClassName="px-0 py-0"
      >
        {user && user.result.two_factor_active ? (
          <Badge color="green">{t('2fa-enabled')}</Badge>
        ) : (
          <Badge color="red">{t('2fa-disabled')}</Badge>
        )}
      </PageHeader>

      <div className="flex items-start">
        <div>
          {user && !user.result.two_factor_active && (
            <div className={'flex items-center'}>
              {!show2faQrCode && (
                <Button
                  onClick={() => setShow2faQrCode(true)}
                  label={t('activate-2fa')}
                  size={'small'}
                  variant={'smoke'}
                />
              )}
            </div>
          )}

          <div>
            <DisplayQrCode show2faQrCode={show2faQrCode} />
          </div>
        </div>

        {show2faQrCode && user && !user.result.two_factor_active && (
          <div className={'flex items-center gap-2'}>
            <input
              id="verify-2fa"
              name="verify-2fa"
              type="text"
              value={token}
              onChange={e => setToken(e.target.value)}
              autoComplete="one-time-code"
              className="block w-full rounded-2xl border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 px-2 bg-app-soft"
              inputMode="numeric"
              pattern="[0-9]*"
            />

            <Button
              label={t('verify')}
              size={'small'}
              variant={'primary'}
              onClick={handleVerify}
            />
          </div>
        )}

        {showDeactivate2fa && (
          <div className={'flex items-center gap-2'}>
            <input
              id="disable-2fa"
              name="disable-2fa"
              type="text"
              value={token}
              onChange={e => setToken(e.target.value)}
              autoComplete="one-time-code"
              className="block w-full rounded-2xl border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 px-2 bg-app-soft"
              placeholder={t('2fa-or-recovery-code')}
            />

            <Button
              label={t('deactivate')}
              size={'small'}
              variant={'danger'}
              onClick={handleDeactivate2fa}
            />
          </div>
        )}

        {user && user.result.two_factor_active && !showDeactivate2fa && (
          <div className={'flex'}>
            <Button
              onClick={() => setShowDeactivate2fa(true)}
              label={t('deactivate-2fa')}
              size={'small'}
              variant={'danger'}
            />
          </div>
        )}
      </div>

      <Modal
        isOpen={showBackupCodes}
        onClose={() => setShowBackupCodes(false)}
        size="large"
        title={t('backup-codes')}
        caption={t('dont-lose-these-codes')}
        footerActions={[
          <Button
            onClick={() => setShowBackupCodes(false)}
            label={t('close-window')}
            size={'small'}
            variant={'smoke'}
          />,
          <Button
            onClick={handleDownloadBackupCodes}
            label={t('download-codes')}
            size={'small'}
            variant={'primary'}
          />
        ]}
      >
        <div className={'flex flex-col gap-2 mt-4 mb-4'}>
          {backupCodes.map((code, index) => (
            <div key={index} className={'flex items-center gap-2'}>
              <span className={'font-medium'}>{index + 1}.</span>
              <span className={'font-medium'}>{code}</span>
            </div>
          ))}
        </div>
      </Modal>
    </Card>
  );
};

export default SecuritySettings;
